import { yupResolver } from '@hookform/resolvers/yup';
import {
  getV1CompaniesCompanyIdOrdersOrderIdAutoAddOnAssignments,
  type ModelError_Item,
  OrderState,
  RateOwnerType,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type DefaultValues,
  type FieldErrors,
  FormProvider,
  type Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import type { FileAttachmentsHandler } from '~formsShared/components/FileAttachments';
import type { FormStateChangeProps } from '~formsShared/types';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import useRecordCompanySettings from '~hooks/useCompanySettings/useRecordCompanySettings';
import type { ContactItem } from '~hooks/useContact/models/ContactItem';
import type { MaterialRate } from '~hooks/useMaterialRates/models';
import type { Order } from '~hooks/useOrders/models';
import type { Phase, Project, ProjectMaterialTotal } from '~hooks/useProjects/models';
import type { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import type { Site, WayPoint } from '~hooks/useSites/models';
import { type DefaultSitesWaypointType, useSites } from '~hooks/useSites/useSites';
import type { TaxRateScheduleTypeahead } from '~hooks/useTaxRateSchedule/models';
import { UserBasic } from '~hooks/useUsers/models';
import type { MaterialRateDetailsHandler } from '~pages/Sales/Orders/NewOrderForm/MaterialRateDetails';
import useRateDetails from '~pages/Sales/Orders/NewOrderForm/useRateDetails';
import {
  createWaypoint,
  orderOwnsSiteRates,
  type RatesSyncStatus,
  resolveRatesSyncStatus,
  resolveSiteRateScheduleTarget,
} from '~pages/Sales/Orders/NewOrderForm/utils';
import {
  makeCustomerSpecificOrderFormSchema,
  type NewOrderFormSchemaInterface,
  setDefaultOrderValues,
} from '~pages/Sales/Orders/newOrderFormSchema';
import type { OrderFormHandle } from '~pages/Sales/Orders/orderFormHandle';
import type { FreightRatesHandler } from '~pages/Sales/Projects/components/FreightRates';
import type { SiteRateScheduleOperations } from '~pages/Sales/Projects/components/SiteRatesForm';
import {
  isFreightRateRequiredForDepartment,
  makeMaterialRateRequirement,
} from '~pages/Sales/sharedSales/rateRequirements';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import type { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { computeSiteFilteredMaterialIds } from './computeSiteFilteredMaterialIds';
import { OrderFormV3Effects } from './effects/OrderFormV3Effects';
import { type FieldMetadata, orderFieldMetadata } from './fieldMetadata';
import {
  type OrderFormV3Preloads,
  useOrderFormV3Preloads,
} from './useOrderFormV3Preloads';

/**
 * The CONCRETE shape `setDefaultOrderValues` seeds. The schema interface
 * types `pickUpWayPoint`/`material` as conditional `{}` (yup `.when`), so
 * waypoint internals only exist on this side of the useForm boundary cast —
 * live watches of those fields are projected through it (the US-006
 * `materialRateInitialIds` precedent, extended to watches).
 */
type OrderSeedValues = ReturnType<typeof setDefaultOrderValues>;

/**
 * The runtime form-slot shape of the three waypoint fields. The schema
 * interface types them as conditional `{}` (yup `.when`), which erases every
 * dot-path the waypoint machinery writes (`pickUpWayPoint.id`,
 * `pickUpWayPoint.contact.name`, …). This is the union of the three shapes
 * the form actually holds: the seed, the SiteSelection pick (createWaypoint's
 * literal — lat/lon/address ride along because RouteEstimateEffects reads
 * them off the watched form value), and the staging destroy marker (a bare
 * `{ id }` that keeps the saved waypoint id around so the deparse layer can
 * emit `_destroy`).
 */
export type OrderWaypointFormValue = {
  id?: string;
  type?: string;
  ordinality?: number;
  contact?: Nullable<{ name?: Nullable<string>; phone?: Nullable<string> }>;
  site?: Nullable<{
    id?: string;
    name?: string;
    lat?: Site['lat'];
    lon?: Site['lon'];
    address?: Site['address'];
    discardedAt?: Nullable<string>;
  }>;
} | null;

/** The watched `pickUpWayPoint.site` / `dropOffWayPoint.site` slice. */
export type OrderWaypointSiteValue = NonNullable<OrderWaypointFormValue>['site'];

/**
 * The SiteSelection display-state shape: WayPoint instances seeded from the
 * defaults and rebuilt by createWaypoint on every pick, plus the
 * contact-merge spread the autofill effect writes and the staging destroy
 * marker. Mirrors the legacy runtime exactly — including the quirk that
 * spreading the getter-backed WayPoint class drops its prototype getters, so
 * a post-autofill value no longer exposes `.site` (mirrored, not fixed).
 */
export type OrderWaypointDisplayValue = Nullable<{
  id?: WayPoint['id'];
  contact?: Nullable<ContactItem | { name?: Nullable<string>; phone?: Nullable<string> }>;
  site?: WayPoint['site'];
}>;

/**
 * Form-value twin of `createWaypoint`, built from the picked Site with the
 * identical literal. createWaypoint casts its literal to the WayPoint class,
 * whose declared getters hide the lat/lon/address the literal actually
 * carries — reading them back off the instance doesn't typecheck, and v3 may
 * not re-cast (a lint rule bans type casts in this directory). Same approach
 * as projectFormV2's createWaypointFormValue, extended with the order form's
 * lat/lon/address pass-through.
 */
function createWaypointFormValue(
  site: Site,
  type: WaypointType,
  ordinality: number,
  waypointID?: string,
  contact?: ContactItem,
): NonNullable<OrderWaypointFormValue> {
  return {
    id: waypointID,
    type,
    ordinality,
    contact: {
      name: contact?.name ?? '',
      phone: contact?.phone ?? '',
    },
    site: {
      id: site.id,
      name: site.name,
      lat: site.lat,
      lon: site.lon,
      address: site.address,
      discardedAt: site.discardedAt?.toISOString() ?? null,
    },
  };
}

/**
 * The runtime shape of an `allContacts` entry. The schema's contact rows
 * carry only name/phone/type/isDeleted, but the seed and the embedded
 * Contacts widget keep the persisted contact `id` on each row — legacy's
 * deparse needs it to emit the `_destroy` marker for deleted contacts (via
 * transformContact), and deparseV3 reads it the same way.
 */
export type OrderContactFormValue = {
  id?: string;
  name?: string;
  phone?: string;
  type?: Nullable<{ name?: string; id?: string }>;
  isDeleted?: boolean;
};

/**
 * The v3 form-state type: the yup schema keys plus three legacy non-schema
 * runtime fields — `jobStartSameAsOrderStart` (the legacy form seeds it; the
 * embedded DateAndTimeV2 reads and writes it), `projectExternalId` (the seed
 * carries it, the project cascade writes it, and the embedded
 * ProjectDetailsV2 renders it read-only) and `phases` (seeded from the
 * persisted order; nothing in the form edits it, but the deparse layer must
 * re-emit it on create so copy-and-edit preserves the source order's
 * phases). Only `phases` is deparsed; none are validated, so they live
 * beside the schema interface rather than inside it.
 *
 * Fourteen fields override their InferType shapes with the RUNTIME types
 * legacy gives the form (its useForm generic is `typeof defaultValues`, i.e.
 * the seed): the quantity trio holds the seed's string|number unions (text
 * inputs hold strings; the project-change cascade copies the project's
 * string values verbatim — converting them would change rendered text, e.g.
 * '120.50' → 120.5), the two project-data arrays hold the model instances
 * legacy declares on its own form state, the three waypoint fields carry
 * OrderWaypointFormValue so the waypoint machinery's dot-path writes
 * typecheck (see its docblock), and six embed-managed fields whose yup
 * `.when` declarations erase their shapes to `{}` (material, equipmentType,
 * unitOfMeasure, jobUnitOfMeasure, equipmentTypeGrossCapacityUnitOfMeasure,
 * allContacts) carry the seed shapes so deparseV3 can read the `.id`/`.value`
 * paths legacy reads without casts. The yup schema still validates all of
 * them — only the static type follows the runtime.
 */
export type OrderFormV3FieldValues = Omit<
  NewOrderFormSchemaInterface,
  | 'quantity'
  | 'jobQuantity'
  | 'equipmentTypeGrossCapacity'
  | 'projectMaterialTotals'
  | 'allPhaseCodes'
  | 'pickUpWayPoint'
  | 'dropOffWayPoint'
  | 'stagingWayPoint'
  | 'material'
  | 'equipmentType'
  | 'unitOfMeasure'
  | 'jobUnitOfMeasure'
  | 'equipmentTypeGrossCapacityUnitOfMeasure'
  | 'allContacts'
> & {
  quantity: OrderSeedValues['quantity'];
  jobQuantity: OrderSeedValues['jobQuantity'];
  equipmentTypeGrossCapacity: OrderSeedValues['equipmentTypeGrossCapacity'];
  projectMaterialTotals: ProjectMaterialTotal[];
  allPhaseCodes: Phase[];
  pickUpWayPoint: OrderWaypointFormValue;
  dropOffWayPoint: OrderWaypointFormValue;
  stagingWayPoint: OrderWaypointFormValue;
  material: OrderSeedValues['material'];
  equipmentType: OrderSeedValues['equipmentType'];
  unitOfMeasure: OrderSeedValues['unitOfMeasure'];
  jobUnitOfMeasure: OrderSeedValues['jobUnitOfMeasure'];
  equipmentTypeGrossCapacityUnitOfMeasure: OrderSeedValues['equipmentTypeGrossCapacityUnitOfMeasure'];
  allContacts: OrderContactFormValue[];
  jobStartSameAsOrderStart: boolean;
  projectExternalId: OrderSeedValues['projectExternalId'];
  phases: OrderSeedValues['phases'];
};

/**
 * Mirrors the legacy `NewOrderFormStateChange` shape (declared there without
 * export): the dirty relay plus the optional default-site params some
 * waypoint flows attach.
 */
export type OrderFormStateChange = FormStateChangeProps & {
  siteId?: string;
  waypointType?: DefaultSitesWaypointType;
};

/**
 * The runtime shape of the `taxRateSchedule` autocomplete value: the schema
 * types the field as the bare `{ label, value }` pair (yup mixed), but the
 * typeahead's parseFn stores the parsed schedule entity beside it
 * (AutocompleteTaxRateSchedulesTypeahead) — the tax auto-populate effect reads
 * the rate off that entity. Legacy widens its watch the same way.
 */
export type TaxRateScheduleOption = {
  _entity?: TaxRateScheduleTypeahead;
  label: string;
  value: string;
};

/** The legacy rate-dropdown fetchers (useRateDetails.ts) FreightRates pages with. */
type RateDetailsFetchers = ReturnType<typeof useRateDetails>;

/**
 * The 5 render-time flags. Property names equal the
 * FeatureFlags enum keys VERBATIM — no aliases — so an agent grepping for
 * the enum key always finds the consumer. Schema-time, defaults-time, and
 * effect-time flags are consumed inside the Provider and intentionally NOT
 * exposed here.
 */
type OrderFormV3Flags = {
  tomlinsonOrderModal: boolean;
  showHaulerRateTypeCard: boolean;
  vendorJobSplittingEnabled: boolean;
  driverAgent: boolean;
  liveEtaEnabled: boolean;
};

type OrderFieldRuntimeMetadata<K extends keyof NewOrderFormSchemaInterface> =
  FieldMetadata & {
    // The RUNTIME field type — OrderFormV3FieldValues overrides five keys
    // with the seed-shaped unions the form actually holds (see its docblock).
    currentValue: OrderFormV3FieldValues[K];
    isDirty: boolean;
    isAIEdited: boolean;
    validationError: string | undefined;
  };

type OrderFormV3Callbacks = {
  /**
   * Relays a dirty signal from embedded widgets that track edits outside RHF
   * (FreightRates wires this in). Mirrors legacy's
   * `onDirtyStatusChange={() => onFormStateChange({ isDirty: true })}`.
   */
  onDirtyStatusChange: () => void;
  /**
   * Context-owned backing for three of the shell's eight OrderFormHandle
   * methods. They live here — not in the shell — because their state
   * (SRS refs, the starred-schedule id, and later the waypoint machinery) is
   * Provider state the panels also reach.
   */
  updateProjectDefaultSites: OrderFormHandle['updateProjectDefaultSites'];
  takePendingSiteRateScheduleOperations: OrderFormHandle['takePendingSiteRateScheduleOperations'];
  applySiteRateSaveResult: OrderFormHandle['applySiteRateSaveResult'];
};

type OrderFormV3ContextValue = {
  flags: OrderFormV3Flags;
  // Company settings, NOT Split flags — `flags` keys mirror FeatureFlags enum
  // keys verbatim, so settings live as their own fields.
  applyRateChangesDefaultOn: boolean;
  showDefaultDigitalTicketView: boolean;
  showServiceClass: boolean;
  defaultOrder: Order | null;
  isCopyAndEdit: boolean;
  /**
   * `Boolean(defaultOrder?.id)` — the form is editing a persisted order.
   * TrucksPanel forwards it to FreightRates, whose edit-mode branches (SRS
   * rate override, copy contract) key on it.
   */
  isEditingOrder: boolean;
  /**
   * The watched `company` form value (legacy `const { company } = watch()`).
   * Subscribed ONCE here so panels can read it without taking their own
   * watch hook (the panel grep gate forbids useWatch/watch in panel files)
   * — the projectFormV2 watchedSelectedCompany pattern.
   */
  watchedCompany: OrderFormV3FieldValues['company'] | undefined;
  /**
   * The full Project entity behind the selected `project` form value. Stays
   * null until the project-change cascade fetches it; the
   * OrderCustomerDetailsPanel embed already consumes it
   * (`project={projectData ?? undefined}`).
   */
  projectData: Project | null;
  /**
   * Material ids allowed under the selected pickup site — null when no site
   * filter applies (the fetched project has no site-bearing material total).
   * Owned here because its two consumers, MaterialsV2 (options filter +
   * default material pick, via MaterialsPanel) and the ProjectDetailsV2
   * cascade, live in SIBLING panels — a single owner keeps the first render
   * filtered and both consumers agreed.
   */
  siteFilteredMaterialIds: Set<string> | null;
  /**
   * The selected project's material rates that apply to the order's customer
   * account, fetched by effects/useProjectMaterialRatesFetch. Purely
   * informational: the materials card prints every candidate rate under each
   * additive material row so a dispatcher can see the project's pricing. An
   * order stores a rate only for its single scalar material, so these never
   * become editable fields.
   */
  projectMaterialRates: MaterialRate[];
  /**
   * The order's first waypoint of each kind, falling back to the fetched
   * project's when the order has none. Re-derived per render, so a project
   * selection that resolves after mount updates them — the location panel
   * (SiteSelection seeds, contact auto-open/auto-fill) consumes all three.
   */
  defaultPickUpWaypoint: WayPoint | undefined;
  defaultDropOffWaypoint: WayPoint | undefined;
  defaultStagingWaypoint: WayPoint | undefined;
  /**
   * The waypoint surface (legacy's watches, display state, contact-section
   * state, and JSX closures — all lifted into the Provider so the panel
   * stays render-only). The watched waypoint SITES gate the contact toggles
   * and the divider; the display trio drives SiteSelection's selected
   * values; the autofill effect (effects/useWaypointContactAutofill) writes
   * the contact lists, the show state and the lastSelected phones through
   * the effects host.
   */
  watchedPickUpWaypointSite: OrderWaypointSiteValue;
  watchedDropOffWaypointSite: OrderWaypointSiteValue;
  pickUpWaypoint: OrderWaypointDisplayValue;
  dropOffWaypoint: OrderWaypointDisplayValue;
  stagingWaypoint: OrderWaypointDisplayValue;
  showPickUpContact: boolean;
  showDropOffContact: boolean;
  pickUpContacts: ContactItem[];
  dropOffContacts: ContactItem[];
  /**
   * Force-remount keys for the two PhoneCodeFlagInput embeds: the phone
   * widget caches its initial value, so picking a site contact bumps the
   * key to re-seed it. The panel's name-autocomplete onSelect writes
   * through the setters exactly like the legacy inline closures.
   */
  lastSelectedPickUpPhone: string;
  lastSelectedDropOffPhone: string;
  setLastSelectedPickUpPhone: Dispatch<SetStateAction<string>>;
  setLastSelectedDropOffPhone: Dispatch<SetStateAction<string>>;
  toggleOnPickUpContact: () => void;
  toggleOffPickUpContact: () => void;
  toggleOnDropOffContact: () => void;
  toggleOffDropOffContact: () => void;
  onPickUpSiteChange: (site?: Site) => void;
  onDropOffSiteChange: (site?: Site) => void;
  onStagingSiteChange: (site?: Site) => void;
  /**
   * SiteSelection persisted a new default site — relay {siteId,
   * waypointType} so OrderForm.tsx refreshes its default-sites cache, and
   * count the add as a form edit.
   */
  onDefaultSiteAdded: (params: {
    siteId: string;
    waypointType: DefaultSitesWaypointType;
  }) => void;
  /**
   * Counterpart of `onDefaultSiteAdded`: the user undid an optimistic
   * default-site addition, so OrderForm.tsx must drop the staged post-create
   * POST for it. No-op when the drawer supplies no handler.
   */
  onTempDefaultSiteRemoved: (params: {
    siteId: string;
    waypointType: DefaultSitesWaypointType;
  }) => void;
  /**
   * Derived requirement booleans the OrderCustomerDetailsPanel embed takes
   * as props. They are derivations, not raw flags, so they live at the
   * context root — `flags.*` carries only the 5 render flags under
   * verbatim enum-key names.
   */
  isServiceClassRequired: boolean;
  shouldRequireOrderName: boolean;
  // Read for the order's company, which can differ from the user's own.
  isDepartmentRequired: boolean;
  isSalespersonRequired: boolean;
  itemizeMaterialRateDefaultOn: boolean;
  isMaterialRateRequired: boolean;
  isFreightRateRequired: boolean;
  keepFreightRatesMounted: boolean;
  isRateByDepartmentNameRuleOn: boolean;
  isTaxRequired: boolean;
  /**
   * Schema-time flag (factory arg 1) that the OrderCustomerDetailsPanel
   * embed ALSO consumes as a prop. Exposed at the root under its verbatim
   * enum-key name; intentionally NOT on `flags`, which is reserved for the
   * 5 render-time flags.
   */
  tomlinsonOrderFormProjectFirst: boolean;
  /**
   * True while a NEW order's `loadAt` sits before today (now
   * effects/usePastLoadAtWarning.ts) — DateAndTimePanel renders the
   * advisory alert from it. Provider state rather than a form field: it is
   * never validated and never deparsed.
   */
  showPastDateWarning: boolean;
  /**
   * Managing material rates needs BOTH the create and edit permissions.
   * MaterialsPanel ANDs it with the visibility state below for the
   * MaterialRateDetails embed's `isVisible` — the show/hide link still
   * renders without permissions, exactly like legacy.
   */
  canManageMaterialRates: boolean;
  /**
   * Whether the actor may edit the project the order belongs to. Gates the
   * "apply rate changes to existing orders" checkbox in TrucksPanel: a rate
   * edit made here writes the PROJECT's card, and the backend honours neither
   * half of the cascade controls without this permission — it forces the
   * per-request apply override to false, and the durable-decouple call raises
   * outright, which aborts the whole save. Showing the checkbox without the
   * permission would offer a choice the backend discards.
   */
  canEditProject: boolean;
  /**
   * Mount-time anchors for the MaterialRateDetails embed: legacy reads
   * `defaultValues.company/material/pickUpWayPoint.site` for the embed's
   * `initial*` props — the auto-selection guard compares live selections
   * against them. Projected from the SEED snapshot, never from live form
   * state (the schema interface types `material` / `pickUpWayPoint` as
   * conditional `{}`, so the seed's concrete helper return is also the only
   * place these ids are typed).
   */
  materialRateInitialIds: {
    companyId: string | undefined;
    materialId: string | undefined;
    pickupSiteId: string | undefined;
  };
  /**
   * Material-rate block visibility: derived from the four material-rate
   * fields by effects/useMaterialRateVisibility.ts and flipped by the
   * panel's show/hide PseudoLink through the toggle below.
   */
  isMaterialRatesBlockVisible: boolean;
  toggleMaterialRatesBlockVisibility: () => void;
  /**
   * Tax-section visibility: pre-opened when the mounted order carries tax
   * data, flipped by TaxPanel's show/hide PseudoLink through the toggle,
   * and auto-opened by the project cascade when a selected project carries
   * tax data (via effects/useProjectCascade).
   */
  isTaxSectionVisible: boolean;
  toggleTaxSectionVisibility: () => void;
  /**
   * The watched `taxRateSchedule` form value: TaxPanel disables the manual
   * rate field while a schedule is selected, and the
   * useTaxRateAutopopulate effect copies the entity's rate into `taxRate`.
   */
  watchedTaxRateSchedule: TaxRateScheduleOption | null | undefined;
  applyAIPatch: (partial: Partial<NewOrderFormSchemaInterface>) => void;
  aiEditedFields: ReadonlySet<keyof NewOrderFormSchemaInterface>;
  getFieldMetadata: <K extends keyof NewOrderFormSchemaInterface>(
    key: K,
  ) => OrderFieldRuntimeMetadata<K>;
  validateField: <K extends keyof NewOrderFormSchemaInterface>(
    key: K,
  ) => Promise<boolean>;
  callbacks: OrderFormV3Callbacks;
  /**
   * Out-of-form preloads (geofence default + the new-order primary customer
   * rate). The loader pair on it is plumbing for the effects host; panels
   * read the resolved values (`primaryCustomerRate` feeds FreightRates'
   * default customer rate).
   */
  preloads: OrderFormV3Preloads;
  /**
   * The watched `project` form value (legacy `const { project } = watch()`)
   * — one subscription for the SRS effects, the rates target resolution,
   * and the panels that need the selection (ProjectDetailsPanel,
   * TrucksPanel).
   */
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  /**
   * The order's service date (`loadAt`, falling back to `jobStartAt`) as a
   * `YYYY-MM-DD` string, or undefined before a date is picked. TrucksPanel
   * hands it to FreightRates so fuel-surcharge add-on rows resolve their
   * displayed rate at the date billing will actually use (the job's start
   * date) instead of today.
   */
  fuelSurchargeRateDate: string | undefined;
  /**
   * Whether this form edits an order in COMPLETED state (Copy & Edit creates
   * a NEW order, so the source's COMPLETED state doesn't count). Completion
   * does not freeze rates; TrucksPanel uses this only to pick the completed
   * order's rate source (its own stamped rules vs the project rate book) for
   * the FreightRates `projectId` / `initialSiteRateSchedules` props.
   */
  isCompletedOrder: boolean;
  /**
   * Project rate defaults apply only to a GENUINELY new order — editing,
   * copy-and-edit, and the saved-state reopen (`orderId` set) all keep the
   * order's own stamped rate values. Sixth argument of the
   * getOrderFormRate* derivations feeding TrucksPanel.
   */
  shouldUseProjectRateDefaults: boolean;
  /**
   * SRS overrides an edited order's stamped rates only after the
   * dispatcher touches the pickup site in this session. The backing
   * `pickupChangedFromSaved` flag is Provider state that stays false until
   * the waypoint effect ports that legacy behavior — the derivation ships
   * with the rates panel so the FreightRates wiring is already exact.
   */
  shouldOverrideRatesWithSrs: boolean;
  /**
   * SRS state the rates panel consumes. An in-progress order "owns" its
   * SRS when stamped rates remain from a prior completion or the order was
   * created without a project — writes must then land on the order, not
   * the project's shared records.
   */
  orderHasOwnSiteRates: boolean;
  projectHasSiteRates: boolean;
  projectSiteRates: SiteRateSchedule | undefined;
  completedOrderRules: SiteRateSchedule[];
  /**
   * TRE-16198: whether the completed order's order-scoped SRS lookup has
   * resolved. `completedOrderRules` starts `[]`, so an empty list alone cannot
   * distinguish "not fetched yet" from "owns no stamped snapshot" — the rates
   * panel needs that distinction before it may fall back to the project rate
   * book. Always false for a non-completed order, which never runs the lookup.
   */
  completedOrderRulesLoaded: boolean;
  /**
   * Has the SRS-presence lookup settled? `projectHasSiteRates` starts false and
   * stays false through two sequential cursor-exhausting fetches, so consumers
   * that treat false as "the project owns no cards" need this to tell that
   * apart from "not resolved yet".
   */
  projectHasSiteRatesLoaded: boolean;
  orderSiteRateSchedules: SiteRateSchedule[];
  siteRateCustomerAddOnAssignments: AddOnAssignment[] | undefined;
  siteRateVendorAddOnAssignments: AddOnAssignment[] | undefined;
  siteRateDriverAddOnAssignments: AddOnAssignment[] | undefined;
  projectCustomerAddOnAssignments: AddOnAssignment[] | undefined;
  projectVendorAddOnAssignments: AddOnAssignment[] | undefined;
  projectDriverAddOnAssignments: AddOnAssignment[] | undefined;
  /** The Freight Rates chip status (via resolveRatesSyncStatus). */
  ratesSyncStatus: RatesSyncStatus;
  /**
   * Freight-rates block visibility: starts expanded, flipped by the panel's
   * link via the toggle.
   */
  isFreightRatesBlockVisible: boolean;
  toggleFreightRatesBlockVisibility: () => void;
  /**
   * The legacy rate-dropdown fetchers, keyed on the watched
   * account/company/project/service ids. Handed to FreightRates.
   */
  fetchCustomerRates: RateDetailsFetchers['fetchCustomerRates'];
  fetchVendorRates: RateDetailsFetchers['fetchVendorRates'];
  fetchDriverRates: RateDetailsFetchers['fetchDriverRates'];
  /**
   * Persisted orders inherit add-ons from their own saved records —
   * curried per owner type, exactly as FreightRates consumes it.
   */
  fetchParentAddOnAssignments: (
    rateOwnerType: RateOwnerType,
  ) => (signal?: AbortSignal) => Promise<AddOnAssignment[]>;
  /**
   * Vendor→driver rate default for new orders: submit's derive step keys
   * on it. The effects update it from project/SRS data; the setter is
   * handed to FreightRates' checkbox.
   */
  useVendorRateForDriver: boolean;
  setUseVendorRateForDriver: Dispatch<SetStateAction<boolean>>;
  /**
   * The starred SRS row id. Merged over the form data at submit — the
   * schema field of the same name only carries the mount-time snapshot.
   * Wired to FreightRates' change callback.
   */
  defaultSiteRateScheduleId: string | null;
  /**
   * FreightRates' star-change callback — records the new starred id for
   * submit and relays the dirty signal (the SRS star lives outside RHF, so
   * RHF's own dirty tracking never sees it).
   */
  onDefaultSiteRateScheduleChange: (id: string | null) => void;
  // Shared handler refs. The owning panel writes each ref; the shell reads
  // it at submit/close time. They live on context because the shell and the
  // panels cannot share a ref otherwise.
  freightRatesRef: React.MutableRefObject<FreightRatesHandler | null>;
  materialRateDetailsRef: React.MutableRefObject<MaterialRateDetailsHandler | null>;
  fileAttachmentsRef: React.MutableRefObject<FileAttachmentsHandler | null>;
  pendingSrsOpsRef: React.MutableRefObject<SiteRateScheduleOperations | null>;
  freightRatesSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  pushErrorAlert: (message: string) => void;
};

const OrderFormV3Context = createContext<OrderFormV3ContextValue | null>(null);

export function useOrderFormV3Context(): OrderFormV3ContextValue {
  const ctx = useContext(OrderFormV3Context);
  if (!ctx) {
    throw new Error('useOrderFormV3Context must be used inside OrderFormV3Provider');
  }
  return ctx;
}

type ProviderProps = {
  defaultOrder: Order | null;
  defaultDate?: string;
  isCopyAndEdit?: boolean;
  onFormStateChange: (change: OrderFormStateChange) => void;
  /**
   * The drawer stages default-site additions for the post-create POSTs, so
   * it also owns un-staging one the user removed before the order exists.
   * MUST be identity-stable (SiteSelection captures it in a
   * narrow-dependency memo).
   */
  onTempDefaultSiteRemoved?: (params: {
    siteId: string;
    waypointType: DefaultSitesWaypointType;
  }) => void;
  /**
   * Save-path 422 errors: OrderForm.tsx's `asyncErrors` state, mapped to
   * inline field errors by effects/useAsyncErrorMapping. They flow into the
   * Provider — not the shell — because the mapping is an effect and the
   * shell is effect-free (the ProjectFormV2Provider `errors` precedent).
   */
  errors?: ModelError_Item[];
  children: ReactNode;
};

type FormProviderProps = ProviderProps & {
  recordCompanySettings: CompanySettings;
  isUserInRecordCompany: boolean;
  onRecordCompanyIdChange: (companyId: string | undefined) => void;
};

// The defaults below are a mount-only snapshot, so the form waits for the order
// company's settings before it mounts.
export function OrderFormV3Provider(props: ProviderProps) {
  const { userStore } = useStores();
  const [recordCompanyId, setRecordCompanyId] = useState<string | undefined>(
    () =>
      props.defaultOrder?.company?.id ||
      (userStore.currentCompanies?.[0] ?? userStore.user?.company)?.id ||
      userStore.userCompany?.id,
  );
  const recordCompanySettings = useRecordCompanySettings(recordCompanyId);

  if (!recordCompanySettings) {
    return <LoadingSpinner isVisible sx={{ height: '100%' }} />;
  }

  return (
    <OrderFormV3FormProvider
      {...props}
      recordCompanySettings={recordCompanySettings}
      isUserInRecordCompany={
        !recordCompanyId || recordCompanyId === userStore.userCompany?.id
      }
      onRecordCompanyIdChange={setRecordCompanyId}
    />
  );
}

function OrderFormV3FormProvider({
  defaultOrder,
  defaultDate,
  isCopyAndEdit,
  onFormStateChange,
  onTempDefaultSiteRemoved: onTempDefaultSiteRemovedProp,
  errors,
  recordCompanySettings,
  isUserInRecordCompany,
  onRecordCompanyIdChange,
  children,
}: FormProviderProps) {
  // 1. Read ALL 9 flags ONCE, FIRST.
  const tomlinsonOrderFormProjectFirst = useFeatureFlag({
    featureFlagKey: FeatureFlags.tomlinsonOrderFormProjectFirst,
  });
  const tomlinsonOrderModal = useFeatureFlag({
    featureFlagKey: FeatureFlags.tomlinsonOrderModal,
  });
  // TRE-15597: generic rate-card flag that swaps freight-rates → hauler-rate-card
  // independently of the tomlinsonOrderModal bundle. The RateDetailsPanel shows
  // the card when either flag is on; validation stays on tomlinsonOrderModal
  // only.
  const showHaulerRateTypeCard = useFeatureFlag({
    featureFlagKey: FeatureFlags.showHaulerRateTypeCard,
  });
  const vendorJobSplittingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.vendorJobSplittingEnabled,
  });
  const orderFormStartTimeDefaultTo7am = useFeatureFlag({
    featureFlagKey: FeatureFlags.orderFormStartTimeDefaultTo7am,
  });
  const orderFormStartTimeDefaultToNull = useFeatureFlag({
    featureFlagKey: FeatureFlags.orderFormStartTimeDefaultToNull,
  });
  const driverAgent = useFeatureFlag({ featureFlagKey: FeatureFlags.driverAgent });
  const liveEtaEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.liveEtaEnabled,
  });
  // Effect-time flag: seeds the vendor→driver state below at mount and feeds
  // the useVendorRateForDriverDerivation effect module.
  const defaultVendorRateToDriver = useFeatureFlag({
    featureFlagKey: FeatureFlags.defaultVendorRateToDriver,
  });

  const { userStore, toasterStore } = useStores();

  const applyRateChangesDefaultOn = userStore.companySettings.isEnabled(
    'apply_rate_changes_default_on',
  );
  const chaseMissingTicketsDefaultOn = userStore.companySettings.isEnabled(
    'chase_missing_tickets_default_on',
  );
  const liveEtaDefault =
    liveEtaEnabled && userStore.companySettings.isEnabled('live_eta_default');
  const runningDefaultOn = userStore.companySettings.isEnabled('running_default_on');
  const showDefaultDigitalTicketView = userStore.companySettings.isEnabled(
    'show_default_digital_ticket_view',
  );
  const showServiceClass = userStore.companySettings.isEnabled('show_service_class');
  const requireOrderName = userStore.companySettings.isEnabled('require_order_name');
  const requireOrderServiceClass = userStore.companySettings.isEnabled(
    'require_order_service_class',
  );
  const isDepartmentRequired = recordCompanySettings.isEnabled('require_department');
  const isSalespersonRequired = recordCompanySettings.isEnabled('require_salesperson');
  const isRateByDepartmentNameRuleOn = recordCompanySettings.isEnabled(
    'require_rates_by_department_name',
  );
  const itemizeMaterialRateDefaultOn = recordCompanySettings.isEnabled(
    'itemize_material_rate_default_on',
  );
  const canPrefillSalesperson =
    isSalespersonRequired &&
    isUserInRecordCompany &&
    Boolean(userStore.user?.isSalesperson);
  const isTaxRequired = recordCompanySettings.isEnabled('require_tax');

  const flags = useMemo<OrderFormV3Flags>(
    () => ({
      tomlinsonOrderModal,
      showHaulerRateTypeCard,
      vendorJobSplittingEnabled,
      driverAgent,
      liveEtaEnabled,
    }),
    [
      tomlinsonOrderModal,
      showHaulerRateTypeCard,
      vendorJobSplittingEnabled,
      driverAgent,
      liveEtaEnabled,
    ],
  );

  // 2. The factory schema — THE deviation from project v2, memoized ONCE
  //    on the schema-time booleans: a fresh schema object per render would
  //    reset resolver state and re-validate everything. The third argument
  //    is the legacy conjunction: requiring a service class only applies while
  //    the field is visible.
  const isServiceClassRequired = showServiceClass && requireOrderServiceClass;
  //    The material-rate block requires both permissions.
  const { canCreateMaterialRate, canEditMaterialRate, canEditProject } =
    userStore.getPermissions();
  const canManageMaterialRates = canCreateMaterialRate && canEditMaterialRate;
  //    The order-name requirement signal the OrderCustomerDetailsPanel
  //    embed consumes as a prop (and the factory consumes as arg 4).
  const shouldRequireOrderName = requireOrderName;
  const schema = useMemo(
    () =>
      makeCustomerSpecificOrderFormSchema(
        tomlinsonOrderFormProjectFirst,
        tomlinsonOrderModal,
        isServiceClassRequired,
        requireOrderName,
        vendorJobSplittingEnabled,
        recordCompanySettings,
        canManageMaterialRates,
      ),
    [
      tomlinsonOrderFormProjectFirst,
      tomlinsonOrderModal,
      isServiceClassRequired,
      requireOrderName,
      vendorJobSplittingEnabled,
      recordCompanySettings,
      canManageMaterialRates,
    ],
  );

  // The runtime schema key-set the 422 mapping matches BE field names
  // against (effects/useAsyncErrorMapping). Derived from the memoized
  // factory output — the flags only swap validators, never add/remove keys,
  // so any variant yields the same set.
  const schemaFieldKeys = useMemo<ReadonlySet<keyof NewOrderFormSchemaInterface>>(
    () => new Set(Object.keys(schema.fields) as Array<keyof NewOrderFormSchemaInterface>),
    [schema],
  );

  // Toast fallback for 422 items no schema key claims (the mapped ones render
  // inline instead). Lives here because effect modules receive plain args —
  // never store handles (the module-graph cycle rule).
  const pushErrorAlert = useCallback(
    (message: string) => {
      toasterStore.push(alert(message, AlertTypes.error));
    },
    [toasterStore],
  );

  const isEditingOrder = Boolean(defaultOrder?.id);
  //    Copy & Edit creates a brand new order, so the source order's
  //    COMPLETED state shouldn't trigger frozen-rate behavior — the new
  //    order loads the project's live SRS, not the frozen snapshot.
  const isCompletedOrder = defaultOrder?.state === OrderState.COMPLETED && !isCopyAndEdit;
  //    Rate defaults fall back to the project's rates only on a genuinely
  //    new order — editing, reopening (`orderId` set) and copy-and-edit
  //    keep the order's stamped values.
  const shouldUseProjectRateDefaults =
    !defaultOrder || (!defaultOrder?.id && !defaultOrder?.orderId && !isCopyAndEdit);

  // 3. Preloads BEFORE useForm: out-of-form fetches — the company
  //    geofence default and the new-order primary customer rate state (its
  //    account/company triggers live in effects/usePrimaryCustomerRateFetch).
  const preloads = useOrderFormV3Preloads({ defaultOrder });

  // 4. Defaults via the EXISTING helper — real signature is 12 POSITIONAL
  //    params; each flag goes in its real position. The helper is shared
  //    with the legacy form — never modify it.
  //
  //    Legacy double-casts userStore.user down to UserBasic for the
  //    copy-and-edit requester override; that cast chain is banned in v3
  //    files, so the Provider constructs a genuine UserBasic from the same
  //    user instead (consumers only read id / name getters, which match).
  const currentCompany = userStore.currentCompanies?.[0] ?? userStore.user?.company;
  const shouldUseKilometers = userStore.userCompany?.isMeters;
  const defaultValues = useMemo(() => {
    const user = userStore.user;
    const base = setDefaultOrderValues(
      // setDefaultOrderValues declares `defaultOrder: Order` but optional-
      // chains every access; legacy passes `defaultOrder as Order` for the
      // same null-on-create case.
      defaultOrder as Order,
      isCopyAndEdit && user
        ? {
            requester: new UserBasic(
              user.id,
              user.firstName,
              user.lastName,
              user.email ?? null,
              user.phone ?? null,
            ),
          }
        : undefined,
      userStore.userCompany,
      currentCompany ?? userStore.userCompany,
      defaultDate,
      orderFormStartTimeDefaultTo7am,
      runningDefaultOn,
      tomlinsonOrderModal,
      chaseMissingTicketsDefaultOn,
      shouldUseKilometers,
      orderFormStartTimeDefaultToNull,
      liveEtaDefault,
      recordCompanySettings,
    );
    const shouldPrefillSalesperson = !defaultOrder && canPrefillSalesperson;

    return {
      ...base,
      // Legacy non-schema defaults, verbatim.
      jobStartSameAsOrderStart: isNil(defaultOrder?.jobStartAt),
      projectMaterialTotals: [],
      allPhaseCodes: [],
      // Absorbs the legacy mount effect: new orders (including
      // copy-and-edit, whose source keeps its id) seed the requester with
      // the current user.
      ...(isEditingOrder || !user ? {} : { requester: user }),
      ...(shouldPrefillSalesperson ? { salespeople: [user] } : {}),
    };
    // The defaults snapshot is taken once at mount, exactly like legacy
    // (useForm ignores defaultValues changes after init); deps cover the
    // mount-time inputs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrder, isCopyAndEdit, defaultDate]);

  //    The MaterialRateDetails embed anchors, projected off the CONCRETE
  //    helper return — the schema interface types material/pickUpWayPoint as
  //    conditional `{}`, so the ids only exist on this side of the useForm
  //    boundary cast below.
  const materialRateInitialIds = useMemo(
    () => ({
      companyId: defaultValues.company?.id,
      materialId: defaultValues.material?.id,
      pickupSiteId: defaultValues.pickUpWayPoint?.site?.id,
    }),
    [defaultValues],
  );

  // yupResolver is typed from the factory schema's own InferType; the form
  // state type adds the non-schema UI helper field. The resolver only
  // validates schema fields and ignores extras at runtime, so the
  // Resolver<OrderFormV3FieldValues> shape is the right boundary cast (no
  // `any`/`unknown` involved). Same pattern as ProjectFormV2Context.tsx.
  const resolver = useMemo<Resolver<OrderFormV3FieldValues>>(
    () => yupResolver(schema) as Resolver<OrderFormV3FieldValues>,
    [schema],
  );

  // 5. The Provider OWNS useForm — nothing above it does. Validation modes
  //    mirror legacy. The seed is the documented boundary cast: the helper
  //    returns the legacy concrete shape (e.g. `'' | Dayjs` dates), which
  //    RHF only accepts as the DeepPartial DefaultValues of the schema
  //    interface.
  const methods = useForm<OrderFormV3FieldValues>({
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as DefaultValues<OrderFormV3FieldValues>,
  });

  // 6. Provider-owned watches — ONE subscription per field for every
  //    consumer (legacy destructures watch() once; panels and effect
  //    modules never take their own for these).
  const watchedCompany = useWatch({ control: methods.control, name: 'company' });
  const watchedAccount = useWatch({ control: methods.control, name: 'account' });
  const watchedProject = useWatch({ control: methods.control, name: 'project' });
  const watchedService = useWatch({ control: methods.control, name: 'service' });
  const watchedLoadAt = useWatch({ control: methods.control, name: 'loadAt' });
  const watchedJobStartAt = useWatch({ control: methods.control, name: 'jobStartAt' });
  const watchedDepartment = useWatch({ control: methods.control, name: 'department' });
  const isMaterialRateRequired = Boolean(
    makeMaterialRateRequirement(
      recordCompanySettings,
      canManageMaterialRates,
    )?.(watchedDepartment),
  );
  const isFreightRateRequired = isFreightRateRequiredForDepartment(
    recordCompanySettings,
    watchedDepartment,
  );
  const [freightRatesWereRequired, setFreightRatesWereRequired] = useState(false);
  if (isFreightRateRequired && !freightRatesWereRequired) {
    setFreightRatesWereRequired(true);
  }
  // Unmounting FreightRates would drop its unsaved card edits.
  const keepFreightRatesMounted = isFreightRateRequired || freightRatesWereRequired;
  //    The service date fuel-surcharge add-on rows resolve their displayed
  //    rate at (legacy: `orderDate = loadAt ?? jobStartAt`). Billing stamps
  //    FSC at the job's start date, so the form must ask the schedule for
  //    that date — not today. The company-timezone `dayjs.tz` conversion
  //    mirrors the job surface (JobFreightRateRow) so both agree on the
  //    date, and the value is formatted here so consumers depend on a
  //    stable string, never a Dayjs instance whose identity changes every
  //    render.
  const watchedServiceStartAt = watchedLoadAt || watchedJobStartAt;
  const parsedServiceStartAt = watchedServiceStartAt
    ? dayjs.tz(watchedServiceStartAt, userStore.userCompany?.timeZone)
    : null;
  const fuelSurchargeRateDate = parsedServiceStartAt?.isValid()
    ? parsedServiceStartAt.format('YYYY-MM-DD')
    : undefined;
  //    The two waypoint-site watches the waypoint machinery keys on.
  //    OrderFormV3FieldValues types the waypoint slots with their runtime
  //    shape (OrderWaypointFormValue), so these subscribe the exact paths
  //    legacy watches — no projection cast needed.
  const watchedPickUpWaypointSite = useWatch({
    control: methods.control,
    name: 'pickUpWayPoint.site',
  });
  const watchedDropOffWaypointSite = useWatch({
    control: methods.control,
    name: 'dropOffWayPoint.site',
  });
  const watchedPickUpWaypointSiteId = watchedPickUpWaypointSite?.id || undefined;
  //    The schema types `project` as the bare {name,id} pair, but the seed
  //    (and the company-reset placeholder) carry `externalId` beside it —
  //    legacy reads it off its seed-typed watch. Same documented
  //    seed-projection boundary as the pickup watch above; the project
  //    cascade copies it into the read-only projectExternalId field.
  const watchedProjectExternalId = (
    (watchedProject ?? undefined) as OrderSeedValues['project'] | undefined
  )?.externalId;
  //    The typeahead stores the parsed schedule entity beside the option
  //    pair while the schema types only the pair — the same documented
  //    widening legacy applies to its watch.
  const watchedTaxRateSchedule = useWatch({
    control: methods.control,
    name: 'taxRateSchedule',
  }) as TaxRateScheduleOption | null | undefined;

  // 7. The legacy rate-dropdown fetchers, rebuilt per the watched ids
  //    exactly like legacy rebuilds them per render.
  const { fetchCustomerRates, fetchVendorRates, fetchDriverRates } = useRateDetails({
    accountId: watchedAccount?.id ?? undefined,
    companyId: watchedCompany?.id ?? '',
    projectId: watchedProject?.id ?? undefined,
    serviceId: watchedService?.id ?? undefined,
  });

  //    The project entity behind the selected `project` form value, fetched
  //    on project change by effects/useProjectDataFetch. The
  //    useProjectCascade module copies its defaults into the form fields.
  const [projectData, setProjectData] = useState<Project | null>(null);

  //    The site-scoped allowed-material set, derived from the fetched
  //    project's material totals and the watched pickup site.
  const siteFilteredMaterialIds = useMemo(
    () =>
      computeSiteFilteredMaterialIds(
        projectData?.projectMaterialTotals ?? [],
        watchedPickUpWaypointSiteId,
      ),
    [projectData?.projectMaterialTotals, watchedPickUpWaypointSiteId],
  );

  //    The selected project's material rates that apply to the order's customer
  //    account, fetched on project/company/account change by
  //    effects/useProjectMaterialRatesFetch. Display-only — the materials card
  //    lists the project's candidate prices per additive material row.
  const [projectMaterialRates, setProjectMaterialRates] = useState<MaterialRate[]>([]);

  //    Per-render waypoint fallbacks — the order's own waypoints win; a
  //    fetched project's fill in for a new order. Seeds SiteSelection and
  //    the contact effects from these.
  const defaultPickUpWaypoint =
    defaultOrder?.pickUpWayPoints?.[0] || projectData?.pickUpWayPoints?.[0];
  const defaultDropOffWaypoint =
    defaultOrder?.dropOffWayPoints?.[0] || projectData?.dropOffWayPoints?.[0];
  const defaultStagingWaypoint =
    defaultOrder?.stagingWayPoints?.[0] || projectData?.stagingWayPoints?.[0];

  //    The SiteSelection display state — what the three selectors show.
  //    Distinct from the form values by legacy design (the display keeps
  //    WayPoint-shaped objects; the form keeps the OrderWaypointFormValue
  //    literals).
  const [pickUpWaypoint, setPickUpWaypoint] =
    useState<OrderWaypointDisplayValue>(defaultPickUpWaypoint);
  const [dropOffWaypoint, setDropOffWaypoint] =
    useState<OrderWaypointDisplayValue>(defaultDropOffWaypoint);
  const [stagingWaypoint, setStagingWaypoint] =
    useState<OrderWaypointDisplayValue>(defaultStagingWaypoint);

  //    The contact-section UI state. The show pair starts closed (the mount
  //    effect opens sections whose default waypoint carries a contact
  //    name); the contact lists and lastSelected phones are written by the
  //    autofill effect through the host and by the panel's
  //    name-autocomplete onSelect.
  const [showPickUpContact, setShowPickUpContact] = useState<boolean>(false);
  const [showDropOffContact, setShowDropOffContact] = useState<boolean>(false);
  const [pickUpContacts, setPickUpContacts] = useState<ContactItem[]>([]);
  const [dropOffContacts, setDropOffContacts] = useState<ContactItem[]>([]);
  const [lastSelectedPickUpPhone, setLastSelectedPickUpPhone] = useState<string>('');
  const [lastSelectedDropOffPhone, setLastSelectedDropOffPhone] = useState<string>('');

  //    The shared post-mount latch. The project cascade's account-effect
  //    flips it on its first (mount) run and gates the department-default
  //    fetch on it; the site-contact effects read the same latch, so it
  //    lives here rather than inside one module.
  const [loaded, setLoaded] = useState(false);

  // 8. SRS + rates state. Written by the effect modules through the host;
  //    read by the rates panel and the derivations below.
  const [projectHasSiteRates, setProjectHasSiteRates] = useState(false);
  const [projectSiteRates, setProjectSiteRates] = useState<SiteRateSchedule | undefined>(
    undefined,
  );
  const [completedOrderRules, setCompletedOrderRules] = useState<SiteRateSchedule[]>([]);
  const [completedOrderRulesLoaded, setCompletedOrderRulesLoaded] = useState(false);
  const [projectHasSiteRatesLoaded, setProjectHasSiteRatesLoaded] = useState(false);
  const [orderSiteRateSchedules, setOrderSiteRateSchedules] = useState<
    SiteRateSchedule[]
  >([]);
  const [siteRateCustomerAddOnAssignments, setSiteRateCustomerAddOnAssignments] =
    useState<AddOnAssignment[] | undefined>();
  const [siteRateVendorAddOnAssignments, setSiteRateVendorAddOnAssignments] = useState<
    AddOnAssignment[] | undefined
  >();
  const [siteRateDriverAddOnAssignments, setSiteRateDriverAddOnAssignments] = useState<
    AddOnAssignment[] | undefined
  >();
  const [projectCustomerAddOnAssignments, setProjectCustomerAddOnAssignments] = useState<
    AddOnAssignment[] | undefined
  >();
  const [projectVendorAddOnAssignments, setProjectVendorAddOnAssignments] = useState<
    AddOnAssignment[] | undefined
  >();
  const [projectDriverAddOnAssignments, setProjectDriverAddOnAssignments] = useState<
    AddOnAssignment[] | undefined
  >();

  //    An in-progress order has its "own" SRS when stamped rates remain from
  //    a prior completion or it was created without a project — the FE must
  //    edit the order's SRS then, not the project's.
  // Pin-clones (pinnedAt set) do NOT count as ownership — a skipped order stays sync-eligible.
  // Dereference-clones (dereferenced true) DO — a durably unchecked order shows its own snapshot.
  const orderHasOwnSiteRates =
    !isCompletedOrder && orderOwnsSiteRates(orderSiteRateSchedules);
  // Pin-clones DO count here, unlike `orderOwnsSiteRates`: the completed-order
  // display already feeds every order-scoped rule (pin-clones included) into
  // FreightRates as `initialSiteRateSchedules`, so ownership for the sync
  // status and the write target must match what the form is showing — a
  // completed order with any order-scoped rule edits its own records.
  const completedOrderHasOwnRates = isCompletedOrder && completedOrderRules.length > 0;
  const ratesSyncStatus = resolveRatesSyncStatus({
    orderHasOwnSiteRates: orderHasOwnSiteRates || completedOrderHasOwnRates,
    projectId: watchedProject?.id,
    projectHasSiteRates,
  });

  //    Sticky flag: flips true once the dispatcher moves the pickup site
  //    away from the saved one — written by the legacy divergence effect,
  //    now effects/useWaypointValidationSync through the host.
  const [pickupChangedFromSaved, setPickupChangedFromSaved] = useState(false);
  //    SRS becomes the rates source of truth on an edited order only after
  //    the pickup diverges from the saved value — reopening without
  //    touching the pickup keeps manual rate edits visible.
  const shouldOverrideRatesWithSrs = isEditingOrder && pickupChangedFromSaved;

  const [isFreightRatesBlockVisible, setIsFreightRatesBlockVisible] = useState(true);
  const toggleFreightRatesBlockVisibility = useCallback(() => {
    setIsFreightRatesBlockVisible((visible) => !visible);
  }, []);

  //    Advisory state: written by the usePastLoadAtWarning effect module
  //    through the host, read by DateAndTimePanel.
  const [showPastDateWarning, setShowPastDateWarning] = useState(false);

  //    Rate-block visibility: written by the useMaterialRateVisibility
  //    effect module through the host, flipped by MaterialsPanel's
  //    PseudoLink via the toggle callback.
  const [isMaterialRatesBlockVisible, setIsMaterialRatesBlockVisible] = useState(false);
  const toggleMaterialRatesBlockVisibility = useCallback(() => {
    setIsMaterialRatesBlockVisible((visible) => !visible);
  }, []);

  //    Tax-section visibility: pre-opened when the mounted order carries
  //    tax data, flipped by TaxPanel's PseudoLink, and auto-opened when a
  //    selected project carries tax data (the setter travels to
  //    useProjectCascade through the effects host).
  const [isTaxSectionVisible, setIsTaxSectionVisible] = useState(
    !isNil(defaultOrder?.taxRate) || !isNil(defaultOrder?.taxRateSchedule),
  );
  const toggleTaxSectionVisibility = useCallback(() => {
    setIsTaxSectionVisible((visible) => !visible);
  }, []);

  // 9. Shared handler refs (legacy parity). Identity-stable until unmount, so
  //    the value memo below doesn't list them as deps.
  const freightRatesRef = useRef<FreightRatesHandler | null>(null);
  const materialRateDetailsRef = useRef<MaterialRateDetailsHandler | null>(null);
  const fileAttachmentsRef = useRef<FileAttachmentsHandler | null>(null);
  const pendingSrsOpsRef = useRef<SiteRateScheduleOperations | null>(null);
  const freightRatesSectionRef = useRef<HTMLDivElement | null>(null);

  //    Submit-time rates state shared by the shell and the rates panel.
  //    The vendor→driver default applies unless the order already carries
  //    a driver rate; the effect modules update it from project/SRS data,
  //    and the setter is handed to FreightRates' checkbox.
  const [useVendorRateForDriver, setUseVendorRateForDriver] = useState(
    defaultVendorRateToDriver &&
      !(defaultOrder?.driverRate?.id || defaultOrder?.driverRateType),
  );
  const [defaultSiteRateScheduleId, setDefaultSiteRateScheduleId] = useState<
    string | null
  >(defaultOrder?.defaultSiteRateSchedule?.id ?? null);

  // 10. AI helpers. Backed by state (not a ref) so the Set's identity
  //    changes on every AI edit and consumers re-render off fresh membership.
  const [aiEditedFields, setAiEditedFields] = useState<
    ReadonlySet<keyof NewOrderFormSchemaInterface>
  >(() => new Set());

  const applyAIPatch = useCallback(
    (partial: Partial<NewOrderFormSchemaInterface>) => {
      const keys = Object.keys(partial) as Array<keyof NewOrderFormSchemaInterface>;
      keys.forEach((key) => {
        const value = partial[key];
        methods.setValue(key, value as OrderFormV3FieldValues[typeof key], {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
      setAiEditedFields((prev) => {
        const next = new Set(prev);
        keys.forEach((key) => next.add(key));
        return next;
      });
    },
    [methods],
  );

  const getFieldMetadata = useCallback(
    <K extends keyof NewOrderFormSchemaInterface>(
      key: K,
    ): OrderFieldRuntimeMetadata<K> => {
      const fieldError = (
        methods.formState.errors as FieldErrors<NewOrderFormSchemaInterface>
      )[key];
      return {
        ...orderFieldMetadata[key],
        currentValue: methods.getValues(key),
        isDirty: Boolean(methods.formState.dirtyFields[key]),
        isAIEdited: aiEditedFields.has(key),
        validationError:
          typeof fieldError?.message === 'string' ? fieldError.message : undefined,
      };
    },
    [methods, aiEditedFields],
  );

  const validateField = useCallback(
    <K extends keyof NewOrderFormSchemaInterface>(key: K): Promise<boolean> =>
      methods.trigger(key),
    [methods],
  );

  // 11. Relay for embedded widgets that track edits outside RHF — mirrors
  //    legacy. Identity-stable so the FreightRates embed doesn't churn its
  //    internal effects.
  const onDirtyStatusChange = useCallback(() => {
    onFormStateChange({ isDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //    The default-sites API surface behind updateProjectDefaultSites (the
  //    autofill effect module takes its own useSites for getSiteContacts).
  const { addSiteToProjectDefaultSites, getOrderDefaultSites, getProjectDefaultSites } =
    useSites();

  // Per-waypoint default-sites sync — when an existing order gained
  // default sites the project doesn't know about, push them onto the
  // project's list after save. Only with a selected project and a
  // persisted order, exactly like legacy.
  const updateProjectDefaultSitesList = useCallback(
    async ({ waypointType }: { waypointType: DefaultSitesWaypointType }) => {
      const projectId = watchedProject?.id;
      if (projectId && defaultOrder) {
        const projectSitesIds = await getProjectDefaultSites({
          projectId,
          waypointType,
          companyId: watchedCompany?.id,
        }).then((sites) => sites.map((site) => site.id));
        const orderSitesIds = await getOrderDefaultSites({
          orderId: defaultOrder?.id,
          waypointType,
          companyId: watchedCompany?.id,
        }).then((sites) => sites.map((site) => site.id));

        const newSiteIds = orderSitesIds.filter(
          (siteId) => !projectSitesIds.includes(siteId),
        );

        if (newSiteIds.length > 0) {
          return await Promise.all(
            newSiteIds.map(
              async (siteId) =>
                await addSiteToProjectDefaultSites({
                  projectId,
                  siteId,
                  waypointType,
                  companyId: watchedCompany?.id,
                }),
            ),
          );
        }
      }
    },
    // Legacy keys on the useSites fetchers too, but they are plain
    // per-render closures over module singletons (connection, the
    // generated client, the store root) — depping them would rebuild this
    // callback, then `callbacks`, then the context value EVERY render (the
    // stability gate). The data inputs stay as deps; the fetchers are read
    // via closure like addSiteToProjectDefaultSites always was.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultOrder, watchedProject?.id, watchedCompany?.id],
  );

  // After a save, sync both waypoint types' default sites onto the
  // project. Fire-and-forget at the handler boundary exactly like legacy
  // (the interface returns void); rejections surface through useSites' own
  // toast-then-rethrow handling.
  const updateProjectDefaultSites = useCallback<
    OrderFormHandle['updateProjectDefaultSites']
  >(async () => {
    await updateProjectDefaultSitesList({ waypointType: WaypointType.PICKUP });
    await updateProjectDefaultSitesList({ waypointType: WaypointType.DROP_OFF });
  }, [updateProjectDefaultSitesList]);

  // The contact-section toggles. Opening only reveals the block; closing
  // clears the contact pair and re-validates it. The clear MUST dirty the
  // waypoint: deparseUpdateV3 only deparses dirty keys, so an undirtied clear
  // never reaches the BE — and when Remove is the user's only edit the PATCH
  // is skipped outright. Legacy got away with the plain setValue because its
  // full body carried the waypoint regardless.
  const toggleOnPickUpContact = useCallback(() => {
    setShowPickUpContact(true);
  }, []);
  const toggleOffPickUpContact = useCallback(() => {
    methods.setValue(
      'pickUpWayPoint.contact',
      { name: '', phone: '' },
      { shouldDirty: true },
    );
    void methods.trigger('pickUpWayPoint.contact');
    setShowPickUpContact(false);
  }, [methods]);
  const toggleOnDropOffContact = useCallback(() => {
    setShowDropOffContact(true);
  }, []);
  const toggleOffDropOffContact = useCallback(() => {
    methods.setValue(
      'dropOffWayPoint.contact',
      { name: '', phone: '' },
      { shouldDirty: true },
    );
    void methods.trigger('dropOffWayPoint.contact');
    setShowDropOffContact(false);
  }, [methods]);

  // A SiteSelection pick rebuilds the waypoint pair — the WayPoint display
  // value (createWaypoint) and the form value (its literal twin) —
  // carrying the DEFAULT waypoint's id/contact forward exactly like
  // legacy; clearing the site nulls both and collapses the contact section.
  const onPickUpSiteChange = useCallback(
    (site?: Site) => {
      let newPickUpWaypoint: OrderWaypointDisplayValue = null;
      let newPickUpFormValue: OrderWaypointFormValue = null;

      if (site) {
        newPickUpWaypoint = createWaypoint(
          site,
          WaypointType.PICKUP,
          10,
          defaultPickUpWaypoint?.id,
          defaultPickUpWaypoint?.contact ?? undefined,
        );
        newPickUpFormValue = createWaypointFormValue(
          site,
          WaypointType.PICKUP,
          10,
          defaultPickUpWaypoint?.id,
          defaultPickUpWaypoint?.contact ?? undefined,
        );
      }

      methods.setValue('pickUpWayPoint', newPickUpFormValue, { shouldDirty: true });
      setPickUpWaypoint(newPickUpWaypoint);
      if (!newPickUpWaypoint) {
        toggleOffPickUpContact();
      }
    },
    [defaultPickUpWaypoint, methods, toggleOffPickUpContact],
  );

  const onDropOffSiteChange = useCallback(
    (site?: Site) => {
      let newDropOffWaypoint: OrderWaypointDisplayValue = null;
      let newDropOffFormValue: OrderWaypointFormValue = null;

      if (site) {
        newDropOffWaypoint = createWaypoint(
          site,
          WaypointType.DROP_OFF,
          20,
          defaultDropOffWaypoint?.id,
          defaultDropOffWaypoint?.contact ?? undefined,
        );
        newDropOffFormValue = createWaypointFormValue(
          site,
          WaypointType.DROP_OFF,
          20,
          defaultDropOffWaypoint?.id,
          defaultDropOffWaypoint?.contact ?? undefined,
        );
      }

      methods.setValue('dropOffWayPoint', newDropOffFormValue, { shouldDirty: true });
      setDropOffWaypoint(newDropOffWaypoint);
      if (!newDropOffWaypoint) {
        toggleOffDropOffContact();
      }
    },
    [defaultDropOffWaypoint, methods, toggleOffDropOffContact],
  );

  // Staging has no contact section; clearing a PERSISTED staging site
  // keeps a bare { id } marker so deparseUpdate sends _destroy — the
  // marker doubles as the display value there, like legacy.
  const onStagingSiteChange = useCallback(
    (site?: Site) => {
      let newStagingWaypoint: OrderWaypointDisplayValue = null;
      let newStagingFormValue: OrderWaypointFormValue = null;

      if (site) {
        newStagingWaypoint = createWaypoint(
          site,
          WaypointType.STAGING,
          15,
          defaultStagingWaypoint?.id,
        );
        newStagingFormValue = createWaypointFormValue(
          site,
          WaypointType.STAGING,
          15,
          defaultStagingWaypoint?.id,
        );
      } else if (defaultStagingWaypoint?.id) {
        const destroyMarker = { id: defaultStagingWaypoint.id };
        newStagingWaypoint = destroyMarker;
        newStagingFormValue = destroyMarker;
      }

      methods.setValue('stagingWayPoint', newStagingFormValue, { shouldDirty: true });
      setStagingWaypoint(newStagingWaypoint);
    },
    [defaultStagingWaypoint, methods],
  );

  // SiteSelection persisted a new default site — relay the params so
  // OrderForm.tsx refreshes its default-sites cache; the add also counts
  // as a form edit. Identity-stable like onDirtyStatusChange.
  const onDefaultSiteAdded = useCallback(
    (params: { siteId: string; waypointType: DefaultSitesWaypointType }) => {
      onFormStateChange({ ...params, isDirty: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Relay the undo of an optimistic default-site addition so the drawer
  // drops the staged post-create POST. Identity-stable like
  // onDefaultSiteAdded — SiteSelection captures it in a narrow-dependency memo.
  const onTempDefaultSiteRemoved = useCallback(
    (params: { siteId: string; waypointType: DefaultSitesWaypointType }) => {
      onTempDefaultSiteRemovedProp?.(params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Take-and-clear the operations the submit assembly stashed, and route
  // the writes to whichever owner the form was reading rates from — a
  // stamped order keeps its edits on the order so they don't silently
  // leak into the project's shared SRS records.
  const takePendingSiteRateScheduleOperations = useCallback<
    OrderFormHandle['takePendingSiteRateScheduleOperations']
  >(() => {
    const ops = pendingSrsOpsRef.current;
    pendingSrsOpsRef.current = null;
    if (!ops) {
      return null;
    }
    // FreightRates stamps the apply-rate-changes checkbox onto the operations
    // whenever the panel offered it, so an explicit `false` is the actor asking
    // for this order to stop tracking the project's rate book. An order that
    // already owns cards is decoupled, so there is nothing left to detach.
    const orderOwnsRates = orderHasOwnSiteRates || completedOrderHasOwnRates;
    const dereferenceFromProject =
      ops.applyRateChangesToExistingOrders === false &&
      !orderOwnsRates &&
      Boolean(watchedProject?.id);
    const target = resolveSiteRateScheduleTarget({
      dereferenceFromProject,
      orderHasOwnSiteRates: orderOwnsRates,
      projectId: watchedProject?.id,
    });
    return { operations: ops, target, dereferenceFromProject };
  }, [orderHasOwnSiteRates, completedOrderHasOwnRates, watchedProject?.id]);

  // Persisted orders page their inherited add-ons from the order's own
  // records — curried per owner type, as FreightRates consumes it.
  const fetchParentAddOnAssignments = useCallback(
    (rateOwnerType: RateOwnerType) =>
      async (signal?: AbortSignal): Promise<AddOnAssignment[]> => {
        if (!defaultOrder?.id || !watchedCompany?.id) {
          return [];
        }

        const response = await getV1CompaniesCompanyIdOrdersOrderIdAutoAddOnAssignments({
          path: { 'company-id': watchedCompany.id, 'order-id': defaultOrder.id },
          query: {
            'filter[account_type]':
              rateOwnerType !== RateOwnerType.DRIVER ? rateOwnerType : undefined,
          },
          signal,
        });

        return response.data.data.map((addOnAssignment) =>
          AddOnAssignment.parse(addOnAssignment),
        );
      },
    [defaultOrder?.id, watchedCompany?.id],
  );

  // When Copy & Edit re-pointed the starred ref at a matched row's
  // clientKey, the SRS POST eventually issues a real server id for that
  // row. Move the starred id onto it so the visual star renders against
  // the saved row and any partial-failure retry PATCH carries the
  // resolved reference instead of the now-orphaned clientKey.
  const applySiteRateSaveResult = useCallback<OrderFormHandle['applySiteRateSaveResult']>(
    (result) => {
      if (defaultSiteRateScheduleId) {
        const starredCreate = result.creates.find(
          (create) =>
            create.clientKey === defaultSiteRateScheduleId &&
            create.serverId &&
            create.serverId !== defaultSiteRateScheduleId,
        );
        if (starredCreate?.serverId) {
          setDefaultSiteRateScheduleId(starredCreate.serverId);
        }
      }
      return freightRatesRef.current?.applySaveResult?.(result);
    },
    [defaultSiteRateScheduleId],
  );

  // Starring an SRS row both records the id the submit assembly merges
  // over the form data and relays the dirty signal — the star lives
  // outside RHF, so nothing else marks the form dirty. Identity-stable for
  // the same reason onDirtyStatusChange is.
  //
  // v3 ALSO writes the schema field, dirtied (legacy never did): the sparse
  // PATCH emits only dirtyFields-gated keys, so without the RHF dirty bit a
  // re-star would silently vanish from the body — the exact data-loss class
  // this write prevents. Legacy never needed it because it re-sends the
  // full payload on every save. The written value equals what the submit
  // merge produces, so payload content is unchanged.
  const onDefaultSiteRateScheduleChange = useCallback((id: string | null) => {
    setDefaultSiteRateScheduleId(id);
    methods.setValue('defaultSiteRateScheduleId', id, { shouldDirty: true });
    onFormStateChange({ isDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callbacks = useMemo<OrderFormV3Callbacks>(
    () => ({
      onDirtyStatusChange,
      updateProjectDefaultSites,
      takePendingSiteRateScheduleOperations,
      applySiteRateSaveResult,
    }),
    [
      onDirtyStatusChange,
      updateProjectDefaultSites,
      takePendingSiteRateScheduleOperations,
      applySiteRateSaveResult,
    ],
  );

  // 12. Effects are NAMED MODULES under ./effects/ — one behavior per file
  //    with the standard header; inline effect hooks are banned in this
  //    file. The render-less OrderFormV3Effects host (mounted inside
  //    <FormProvider> below) calls every module in fixed, explicit order.

  const value = useMemo<OrderFormV3ContextValue>(
    () => ({
      flags,
      applyRateChangesDefaultOn,
      showDefaultDigitalTicketView,
      showServiceClass,
      defaultOrder,
      isCopyAndEdit: Boolean(isCopyAndEdit),
      isEditingOrder,
      watchedCompany,
      projectData,
      siteFilteredMaterialIds,
      projectMaterialRates,
      defaultPickUpWaypoint,
      defaultDropOffWaypoint,
      defaultStagingWaypoint,
      watchedPickUpWaypointSite,
      watchedDropOffWaypointSite,
      pickUpWaypoint,
      dropOffWaypoint,
      stagingWaypoint,
      showPickUpContact,
      showDropOffContact,
      pickUpContacts,
      dropOffContacts,
      lastSelectedPickUpPhone,
      lastSelectedDropOffPhone,
      setLastSelectedPickUpPhone,
      setLastSelectedDropOffPhone,
      toggleOnPickUpContact,
      toggleOffPickUpContact,
      toggleOnDropOffContact,
      toggleOffDropOffContact,
      onPickUpSiteChange,
      onDropOffSiteChange,
      onStagingSiteChange,
      onDefaultSiteAdded,
      onTempDefaultSiteRemoved,
      isServiceClassRequired,
      shouldRequireOrderName,
      isDepartmentRequired,
      isSalespersonRequired,
      itemizeMaterialRateDefaultOn,
      isMaterialRateRequired,
      isFreightRateRequired,
      keepFreightRatesMounted,
      isRateByDepartmentNameRuleOn,
      isTaxRequired,
      tomlinsonOrderFormProjectFirst,
      showPastDateWarning,
      canManageMaterialRates,
      canEditProject,
      materialRateInitialIds,
      isMaterialRatesBlockVisible,
      toggleMaterialRatesBlockVisibility,
      isTaxSectionVisible,
      toggleTaxSectionVisibility,
      watchedTaxRateSchedule,
      applyAIPatch,
      aiEditedFields,
      getFieldMetadata,
      validateField,
      callbacks,
      preloads,
      watchedProject,
      fuelSurchargeRateDate,
      isCompletedOrder,
      shouldUseProjectRateDefaults,
      shouldOverrideRatesWithSrs,
      orderHasOwnSiteRates,
      projectHasSiteRates,
      projectSiteRates,
      completedOrderRules,
      completedOrderRulesLoaded,
      projectHasSiteRatesLoaded,
      orderSiteRateSchedules,
      siteRateCustomerAddOnAssignments,
      siteRateVendorAddOnAssignments,
      siteRateDriverAddOnAssignments,
      projectCustomerAddOnAssignments,
      projectVendorAddOnAssignments,
      projectDriverAddOnAssignments,
      ratesSyncStatus,
      isFreightRatesBlockVisible,
      toggleFreightRatesBlockVisibility,
      fetchCustomerRates,
      fetchVendorRates,
      fetchDriverRates,
      fetchParentAddOnAssignments,
      useVendorRateForDriver,
      setUseVendorRateForDriver,
      defaultSiteRateScheduleId,
      onDefaultSiteRateScheduleChange,
      freightRatesRef,
      materialRateDetailsRef,
      fileAttachmentsRef,
      pendingSrsOpsRef,
      freightRatesSectionRef,
      pushErrorAlert,
    }),
    [
      flags,
      applyRateChangesDefaultOn,
      showDefaultDigitalTicketView,
      showServiceClass,
      defaultOrder,
      isCopyAndEdit,
      isEditingOrder,
      watchedCompany,
      projectData,
      siteFilteredMaterialIds,
      projectMaterialRates,
      defaultPickUpWaypoint,
      defaultDropOffWaypoint,
      defaultStagingWaypoint,
      watchedPickUpWaypointSite,
      watchedDropOffWaypointSite,
      pickUpWaypoint,
      dropOffWaypoint,
      stagingWaypoint,
      showPickUpContact,
      showDropOffContact,
      pickUpContacts,
      dropOffContacts,
      lastSelectedPickUpPhone,
      lastSelectedDropOffPhone,
      toggleOnPickUpContact,
      toggleOffPickUpContact,
      toggleOnDropOffContact,
      toggleOffDropOffContact,
      onPickUpSiteChange,
      onDropOffSiteChange,
      onStagingSiteChange,
      onDefaultSiteAdded,
      onTempDefaultSiteRemoved,
      isServiceClassRequired,
      shouldRequireOrderName,
      isDepartmentRequired,
      isSalespersonRequired,
      itemizeMaterialRateDefaultOn,
      isMaterialRateRequired,
      isFreightRateRequired,
      keepFreightRatesMounted,
      isRateByDepartmentNameRuleOn,
      isTaxRequired,
      tomlinsonOrderFormProjectFirst,
      showPastDateWarning,
      canManageMaterialRates,
      canEditProject,
      materialRateInitialIds,
      isMaterialRatesBlockVisible,
      toggleMaterialRatesBlockVisibility,
      isTaxSectionVisible,
      toggleTaxSectionVisibility,
      watchedTaxRateSchedule,
      applyAIPatch,
      aiEditedFields,
      getFieldMetadata,
      validateField,
      callbacks,
      preloads,
      watchedProject,
      fuelSurchargeRateDate,
      isCompletedOrder,
      shouldUseProjectRateDefaults,
      shouldOverrideRatesWithSrs,
      orderHasOwnSiteRates,
      projectHasSiteRates,
      projectSiteRates,
      completedOrderRules,
      completedOrderRulesLoaded,
      projectHasSiteRatesLoaded,
      orderSiteRateSchedules,
      siteRateCustomerAddOnAssignments,
      siteRateVendorAddOnAssignments,
      siteRateDriverAddOnAssignments,
      projectCustomerAddOnAssignments,
      projectVendorAddOnAssignments,
      projectDriverAddOnAssignments,
      ratesSyncStatus,
      isFreightRatesBlockVisible,
      toggleFreightRatesBlockVisibility,
      fetchCustomerRates,
      fetchVendorRates,
      fetchDriverRates,
      fetchParentAddOnAssignments,
      useVendorRateForDriver,
      defaultSiteRateScheduleId,
      onDefaultSiteRateScheduleChange,
      pushErrorAlert,
    ],
  );

  // 13. FormProvider OUTER, Context.Provider CHILD — the shell and panels
  //    consume via useFormContext (matches the ProjectFormV2Context.tsx
  //    precedent).
  return (
    <FormProvider {...methods}>
      <OrderFormV3Context.Provider value={value}>
        <OrderFormV3Effects
          defaultOrder={defaultOrder}
          onFormStateChange={onFormStateChange}
          applyAIPatch={applyAIPatch}
          aiEditedFields={aiEditedFields}
          validateField={validateField}
          asyncErrors={errors}
          schemaFieldKeys={schemaFieldKeys}
          pushErrorAlert={pushErrorAlert}
          setShowPastDateWarning={setShowPastDateWarning}
          setIsMaterialRatesBlockVisible={setIsMaterialRatesBlockVisible}
          setIsTaxSectionVisible={setIsTaxSectionVisible}
          watchedProjectExternalId={watchedProjectExternalId}
          loaded={loaded}
          setLoaded={setLoaded}
          isCopyAndEdit={Boolean(isCopyAndEdit)}
          isCompletedOrder={isCompletedOrder}
          isEditingOrder={isEditingOrder}
          defaultVendorRateToDriver={defaultVendorRateToDriver}
          watchedProject={watchedProject}
          watchedPickUpWaypointSiteId={watchedPickUpWaypointSiteId}
          watchedPickUpWaypointSite={watchedPickUpWaypointSite}
          watchedDropOffWaypointSite={watchedDropOffWaypointSite}
          watchedCompanyId={watchedCompany?.id}
          onRecordCompanyIdChange={onRecordCompanyIdChange}
          canPrefillSalesperson={canPrefillSalesperson}
          itemizeMaterialRateDefaultOn={itemizeMaterialRateDefaultOn}
          currentUser={userStore.user}
          watchedAccountId={watchedAccount?.id || undefined}
          defaultPickUpWaypoint={defaultPickUpWaypoint}
          defaultDropOffWaypoint={defaultDropOffWaypoint}
          pickupChangedFromSaved={pickupChangedFromSaved}
          setPickupChangedFromSaved={setPickupChangedFromSaved}
          setShowPickUpContact={setShowPickUpContact}
          setShowDropOffContact={setShowDropOffContact}
          setPickUpContacts={setPickUpContacts}
          setDropOffContacts={setDropOffContacts}
          setLastSelectedPickUpPhone={setLastSelectedPickUpPhone}
          setLastSelectedDropOffPhone={setLastSelectedDropOffPhone}
          setPickUpWaypoint={setPickUpWaypoint}
          setDropOffWaypoint={setDropOffWaypoint}
          projectData={projectData}
          setProjectData={setProjectData}
          setProjectMaterialRates={setProjectMaterialRates}
          projectHasSiteRates={projectHasSiteRates}
          setProjectHasSiteRates={setProjectHasSiteRates}
          projectSiteRates={projectSiteRates}
          setProjectSiteRates={setProjectSiteRates}
          completedOrderRules={completedOrderRules}
          setCompletedOrderRulesLoaded={setCompletedOrderRulesLoaded}
          setProjectHasSiteRatesLoaded={setProjectHasSiteRatesLoaded}
          setCompletedOrderRules={setCompletedOrderRules}
          setOrderSiteRateSchedules={setOrderSiteRateSchedules}
          setSiteRateCustomerAddOnAssignments={setSiteRateCustomerAddOnAssignments}
          setSiteRateVendorAddOnAssignments={setSiteRateVendorAddOnAssignments}
          setSiteRateDriverAddOnAssignments={setSiteRateDriverAddOnAssignments}
          setProjectCustomerAddOnAssignments={setProjectCustomerAddOnAssignments}
          setProjectVendorAddOnAssignments={setProjectVendorAddOnAssignments}
          setProjectDriverAddOnAssignments={setProjectDriverAddOnAssignments}
          setUseVendorRateForDriver={setUseVendorRateForDriver}
          setIsFreightRatesBlockVisible={setIsFreightRatesBlockVisible}
          watchedTaxRateSchedule={watchedTaxRateSchedule}
          primaryCustomerRate={preloads.primaryCustomerRate ?? null}
          loadPrimaryCustomerRate={preloads.loadPrimaryCustomerRate}
          clearPrimaryCustomerRate={preloads.clearPrimaryCustomerRate}
          geofenceDelayDefault={preloads.geofenceDelayDefault ?? null}
          initialCycleDistance={defaultValues.cycleDistance ?? undefined}
          initialCycleTime={defaultValues.cycleTime ?? undefined}
          initialPickupToDropoff={defaultValues.pickupToDropoff ?? undefined}
        />
        {children}
      </OrderFormV3Context.Provider>
    </FormProvider>
  );
}
